import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { AuditLogPage } from "./admin-audit";
import { api, platformRequest } from "./client";
import type { UserRole } from "./schema";

export interface PlatformOperator {
  id: string;
  email: string;
  full_name: string;
  is_active: boolean;
  last_login_at: string | null;
  created_at?: string | null;
}

/** The signed-in operator, and which console features this deployment opens. */
export interface PlatformSession extends PlatformOperator {
  impersonation_enabled: boolean;
}

export type OrganizationStatus = "ACTIVE" | "SUSPENDED" | "CLOSED";
export type UsagePlan = "STARTER" | "STANDARD" | "SCALE";

export interface PlatformOrganization {
  id: string;
  name: string;
  slug: string | null;
  status: OrganizationStatus;
  status_reason: string | null;
  status_changed_at: string | null;
  purge_scheduled_at: string | null;
  created_at: string | null;
}

export interface PlatformOrganizationSummary extends PlatformOrganization {
  usage_plan: UsagePlan | null;
  active_member_count: number;
  dossier_count: number;
}

export interface PlatformUser {
  id: string;
  organization_id: string;
  email: string;
  full_name: string;
  role: UserRole;
  is_active: boolean;
  has_2fa: boolean;
  last_login_at: string | null;
  locked_until: string | null;
}

export interface PlatformQuotas {
  monthly_llm_token_quota: number | null;
  monthly_llm_cost_quota_eur: number | null;
  monthly_transcription_minute_quota: number | null;
  storage_byte_quota: number | null;
  user_quota: number | null;
  dossier_quota: number | null;
}

export type QuotaField = keyof PlatformQuotas;

export interface PlatformUsage {
  period_start: string;
  period_end: string;
  llm_calls: number;
  llm_tokens: number;
  llm_estimated_cost_eur: number;
  transcription_minutes: number;
  storage_bytes: number;
  active_users: number;
  dossier_count: number;
  warning_threshold_reached: boolean;
  hard_limit_reached: boolean;
}

export interface PlatformOperatorAction {
  id: string;
  operator_id: string;
  operator_name: string | null;
  action: string;
  target_organization_id: string | null;
  target_organization_name: string | null;
  target_user_id: string | null;
  target_operator_id: string | null;
  reason: string;
  detail: Record<string, unknown> | null;
  created_at: string;
}

export interface PlatformImpersonationHistory {
  id: string;
  operator_name: string;
  target_user_id: string | null;
  target_user_name: string | null;
  reason: string;
  support_reference: string;
  status: ImpersonationSession["status"];
  created_at: string;
  activated_at: string | null;
  expires_at: string;
  ended_at: string | null;
  end_reason: string | null;
}

export interface PlatformOrganizationDetail {
  organization: PlatformOrganizationSummary;
  contact_email: string | null;
  onboarding_completed_at: string | null;
  quotas: PlatformQuotas;
  quotas_match_plan: boolean;
  usage: PlatformUsage;
  members: PlatformUser[];
  actions: PlatformOperatorAction[];
  impersonation_sessions: PlatformImpersonationHistory[];
}

export interface TotpRotationStart {
  otpauth_uri: string;
  qr_png_base64: string;
  expires_at: string;
}

export interface ImpersonationSession {
  id: string;
  operator: PlatformOperator;
  target_user: PlatformUser;
  organization: PlatformOrganization;
  reason: string;
  support_reference: string;
  status: "PENDING_NOTIFICATION" | "ACTIVE" | "ENDED" | "EXPIRED" | "REVOKED";
  notification_status: "PENDING" | "SENT" | "FAILED";
  notification_sent_at: string | null;
  activated_at: string | null;
  expires_at: string;
  ended_at: string | null;
  end_reason: string | null;
}

export interface ImpersonationContext {
  session_id: string;
  operator_id: string;
  operator_name: string;
  target_user_id: string;
  target_user_name: string;
  organization_id: string;
  organization_name: string;
  reason: string;
  support_reference: string;
  expires_at: string;
}

export type LifecycleAction = "suspend" | "reactivate" | "close" | "reopen";

/** What operator sign-in asks for: the TOTP code, unless 2FA is paused. */
export interface PlatformAuthPolicy {
  totp_enabled: boolean;
}

export const platformApi = {
  authPolicy: () => platformRequest.get<PlatformAuthPolicy>("/platform/auth/policy"),
  /** Leave `totpCode` out while 2FA is paused: the password alone signs in. */
  login: (email: string, password: string, totpCode?: string) =>
    platformRequest.post<PlatformSession>("/platform/auth/login", {
      email,
      password,
      ...(totpCode ? { totp_code: totpCode } : {}),
    }),
  logout: () => platformRequest.post<void>("/platform/auth/logout"),
  me: () => platformRequest.get<PlatformSession>("/platform/auth/me"),
  organizations: () =>
    platformRequest.get<PlatformOrganizationSummary[]>("/platform/organizations"),
  organization: (organizationId: string) =>
    platformRequest.get<PlatformOrganizationDetail>(`/platform/organizations/${organizationId}`),
  users: (organizationId: string) =>
    platformRequest.get<PlatformUser[]>(`/platform/organizations/${organizationId}/users`),
  changeStatus: (
    organizationId: string,
    action: LifecycleAction,
    payload: { reason: string; confirmation_name?: string },
  ) =>
    platformRequest.post<PlatformOrganization>(
      `/platform/organizations/${organizationId}/${action}`,
      payload,
    ),
  updateQuotas: (organizationId: string, quotas: Partial<PlatformQuotas>, reason: string) =>
    platformRequest.patch<PlatformOrganizationSummary>(
      `/platform/organizations/${organizationId}/quotas`,
      { quotas, reason },
    ),
  applyPlan: (organizationId: string, plan: UsagePlan, reason: string) =>
    platformRequest.post<PlatformOrganizationSummary>(
      `/platform/organizations/${organizationId}/plan`,
      { plan, reason },
    ),
  unlockMember: (organizationId: string, userId: string, reason: string) =>
    platformRequest.post<PlatformUser>(
      `/platform/organizations/${organizationId}/members/${userId}/unlock`,
      { reason },
    ),
  securityEvents: (organizationId: string, limit: number) =>
    platformRequest.get<AuditLogPage>(
      `/platform/organizations/${organizationId}/security-events?limit=${limit}`,
    ),
  changeMemberEmail: (organizationId: string, userId: string, email: string, reason: string) =>
    platformRequest.post<PlatformUser>(
      `/platform/organizations/${organizationId}/members/${userId}/email`,
      { email, reason },
    ),
  resetMember2fa: (organizationId: string, userId: string, reason: string) =>
    platformRequest.post<PlatformUser>(
      `/platform/organizations/${organizationId}/members/${userId}/reset-2fa`,
      { reason },
    ),
  operators: () => platformRequest.get<PlatformOperator[]>("/platform/operators"),
  setOperatorActive: (operatorId: string, active: boolean, reason: string) =>
    platformRequest.post<PlatformOperator>(
      `/platform/operators/${operatorId}/${active ? "reactivate" : "deactivate"}`,
      { reason },
    ),
  operatorActions: (limit = 100) =>
    platformRequest.get<PlatformOperatorAction[]>(`/platform/operator-actions?limit=${limit}`),
  startTotpRotation: () => platformRequest.post<TotpRotationStart>("/platform/auth/totp-rotation"),
  confirmTotpRotation: (code: string) =>
    platformRequest.post<PlatformOperator>("/platform/auth/totp-rotation/confirm", { code }),
  startImpersonation: (payload: {
    target_user_id: string;
    reason: string;
    support_reference: string;
  }) => platformRequest.post<ImpersonationSession>("/platform/impersonation-sessions", payload),
  /** `null` hors session de support (l'API répond 204). */
  currentImpersonation: async () =>
    (await api.get<ImpersonationContext | undefined>("/platform/impersonation/current")) ?? null,
  stopImpersonation: () => api.post<void>("/platform/impersonation/current/stop"),
};

export const platformKeys = {
  all: ["platform"] as const,
  session: () => ["platform", "session"] as const,
  authPolicy: () => ["platform", "session", "policy"] as const,
  organizations: () => ["platform", "organizations"] as const,
  organization: (id: string) => ["platform", "organizations", id] as const,
  securityEvents: (id: string, limit: number) =>
    ["platform", "organizations", id, "security-events", limit] as const,
  operators: () => ["platform", "operators"] as const,
  operatorActions: () => ["platform", "operator-actions"] as const,
};

export function usePlatformSession() {
  return useQuery({
    queryKey: platformKeys.session(),
    queryFn: () => platformApi.me(),
    retry: false,
    staleTime: 60 * 1000,
  });
}

export function usePlatformAuthPolicy() {
  return useQuery({
    queryKey: platformKeys.authPolicy(),
    queryFn: () => platformApi.authPolicy(),
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
}

/** Sign-in keeps asking for the code until the policy says otherwise. */
export function usePlatformTotpRequired(): boolean {
  return usePlatformAuthPolicy().data?.totp_enabled ?? true;
}

export function usePlatformOrganizations() {
  return useQuery({
    queryKey: platformKeys.organizations(),
    queryFn: () => platformApi.organizations(),
    retry: false,
  });
}

export function usePlatformOrganization(organizationId: string) {
  return useQuery({
    queryKey: platformKeys.organization(organizationId),
    queryFn: () => platformApi.organization(organizationId),
    retry: false,
  });
}

export function usePlatformSecurityEvents(organizationId: string, limit: number) {
  return useQuery({
    queryKey: platformKeys.securityEvents(organizationId, limit),
    queryFn: () => platformApi.securityEvents(organizationId, limit),
    retry: false,
    placeholderData: (previous) => previous,
  });
}

export function usePlatformOperators() {
  return useQuery({
    queryKey: platformKeys.operators(),
    queryFn: () => platformApi.operators(),
    retry: false,
  });
}

export function usePlatformOperatorActions() {
  return useQuery({
    queryKey: platformKeys.operatorActions(),
    queryFn: () => platformApi.operatorActions(),
    retry: false,
  });
}

/** Refresh every console view after a change: lists, the cabinet, the journal. */
export function useInvalidatePlatform() {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({
      predicate: (query) => query.queryKey[0] === "platform" && query.queryKey[1] !== "session",
    });
}

export function usePlatformLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; password: string; totpCode?: string }) =>
      platformApi.login(input.email, input.password, input.totpCode),
    onSuccess: (session) => {
      queryClient.setQueryData(platformKeys.session(), session);
    },
  });
}
